import PlaylistItem from '../../models/playlistItem/playlistItem';
import Source from '../../models/source';

/**
 * Where the last session left off: which track, and how far into it.
 *
 * Deliberately in `localStorage` next to the active playlist rather than in
 * the user's cloud app data. A playhead is worth nothing a minute later, it
 * changes several times a second, and the app-data queues coalesce writes
 * over 15 seconds to 5 minutes precisely because that file is not for this
 * kind of thing. It is also per-device on purpose: resuming a phone at the
 * point a laptop reached would be surprising, and there is no session
 * ownership anywhere in the app to decide which device is right.
 *
 * The queue itself is already persisted - `activePlaylist` writes it under
 * `Constants.ActivePlaylistKey` - so only the pointer into it lives here.
 */
export interface NowPlaying {
  /** The track id as its own source knows it. */
  trackId: string;
  trackType: Source;
  /**
   * Where the track sat in the active playlist. Only a hint: the queue may
   * have been edited since, so the id is what decides, and this saves a
   * scan in the usual case where nothing moved.
   */
  index: number;
  positionMs: number;
  /** 0 when the source never said - Mixcloud before its first progress event. */
  durationMs: number;
  savedAt: number;
}

/** LocalStorage key. */
export const nowPlayingKey = 'nowPlaying';

/**
 * Below this, there is nothing to restore: the track has barely started and
 * dropping the user two seconds in reads as a glitch rather than as memory.
 */
const minResumeMs = 5000;

/**
 * How close to the end still counts as finished. Proportional, with a
 * ceiling, because 15 seconds is a sensible tail on a two-hour mix and half
 * the track on a 30-second SoundCloud preview.
 */
function endGuardMs(durationMs: number) {
  return Math.min(15000, durationMs * 0.05);
}

export function loadNowPlaying(): NowPlaying | undefined {
  try {
    const raw = localStorage.getItem(nowPlayingKey);

    if (!raw) {
      return undefined;
    }

    const saved = JSON.parse(raw) as NowPlaying;

    // Anything written by an older shape, or by hand, is not worth a crash on
    // startup - and startup is exactly when this is read.
    if (
      !saved ||
      typeof saved.trackId !== 'string' ||
      typeof saved.trackType !== 'number' ||
      typeof saved.positionMs !== 'number'
    ) {
      return undefined;
    }

    return saved;
  } catch (err) {
    console.debug('Unable to read the saved playhead', err);

    return undefined;
  }
}

export function saveNowPlaying(state: NowPlaying) {
  try {
    localStorage.setItem(nowPlayingKey, JSON.stringify(state));
  } catch (err) {
    // A full or disabled localStorage must not take playback down with it.
    console.debug('Unable to save the playhead', err);
  }
}

export function clearNowPlaying() {
  try {
    localStorage.removeItem(nowPlayingKey);
  } catch (err) {
    console.debug('Unable to clear the playhead', err);
  }
}

/** Whether a saved entry is about this very track. */
export function isSameTrack(saved: NowPlaying | undefined, item: PlaylistItem) {
  return !!saved && saved.trackId === item.id && saved.trackType === item.trackType;
}

/**
 * The position to start `item` at, or 0 for "from the beginning".
 *
 * A track left within a few seconds of either end starts over: the head of a
 * track holds nothing to return to, and the tail was finished.
 */
export function resumePositionMs(saved: NowPlaying | undefined, item: PlaylistItem) {
  if (!isSameTrack(saved, item)) {
    return 0;
  }

  const { positionMs, durationMs } = saved!;

  if (!(positionMs >= minResumeMs)) {
    return 0;
  }

  if (durationMs > 0 && positionMs > durationMs - endGuardMs(durationMs)) {
    return 0;
  }

  return positionMs;
}

/**
 * Where the saved track sits in the queue as it stands now, or -1 if it is no
 * longer there. The saved index is tried first and the id is what decides,
 * so a queue edited since the last session still resumes the right track.
 */
export function resumeIndex(tracks: PlaylistItem[], saved: NowPlaying) {
  function matches(item: PlaylistItem) {
    return !!item && item.id === saved.trackId && item.trackType === saved.trackType;
  }

  if (saved.index >= 0 && saved.index < tracks.length && matches(tracks[saved.index])) {
    return saved.index;
  }

  return tracks.findIndex(matches);
}

/**
 * Whether this source plays through the shared `<audio>` element, as opposed
 * to an embed with a playhead of its own.
 */
export function usesAudioElement(trackType: Source) {
  switch (trackType) {
    case Source.File:
    case Source.OneDrive:
    case Source.GoogleDrive:
    case Source.Dropbox:
    case Source.SoundCloud:
    case Source.Podcast:
      return true;

    default:
      return false;
  }
}
